/**
 * Detection Gate
 *
 * Two pure helpers used to decide whether an echo-correlation peak is
 * good enough to surface as a detection:
 *
 *   - findEnvelopePeak: argmax an envelope, map to the range-profile bin
 *     space using the same convention as buildRangeProfileFromCorrelation.
 *     Returns null when the peak falls outside [minRange, maxRange] or the
 *     envelope is empty.
 *
 *   - evaluateDetection: combine phase coherence and a linear (not dB)
 *     SNR threshold into a pass/fail with structured reasons. The two
 *     checks are independent — both, either, or neither may fail — and
 *     the reasons[] array lists every failed check.
 *
 * Pure-function module — no I/O, no global state.
 */

/** Mapped peak location in the envelope and range-profile bin spaces. */
export interface EnvelopePeakLocation {
  /** Index into the envelope array. */
  envelopeBin: number;
  /** envelope[envelopeBin]. */
  envelopeValue: number;
  /** Mapped range-profile bin in [0, heatBins-1]. */
  rangeBin: number;
  /** Range in metres. */
  range: number;
  /** First envelope sample inside the [minRange, maxRange] search window. */
  windowStart: number;
  /** Last envelope sample inside the [minRange, maxRange] search window. */
  windowEnd: number;
}

/** Pass/fail decision plus structured diagnostics. */
export interface DetectionQuality {
  /** True when both phase-coherence and SNR thresholds pass. */
  pass: boolean;
  /** Phase coherence value used in the decision. */
  phaseCoherence: number;
  /** Linear SNR = peakValue / max(profileMedian, eps). */
  snr: number;
  /** List of failed checks. Empty when pass=true. */
  reasons: string[];
}

/** Arguments for {@link findEnvelopePeak}. */
export interface FindEnvelopePeakArgs {
  envelope: Float32Array;
  /** Correlation tau offset (matches `CorrelationResult.tau0`). */
  tau0: number;
  sampleRate: number;
  /** Speed of sound (m/s). */
  speedOfSound: number;
  /** Lower bound of the range profile (m). */
  minRange: number;
  /** Upper bound of the range profile (m). */
  maxRange: number;
  /** Number of range-profile bins. */
  heatBins: number;
}

/** Arguments for {@link evaluateDetection}. */
export interface EvaluateDetectionArgs {
  phaseCoherence: number;
  peakValue: number;
  profileMedian: number;
  /** Default 0.4. */
  coherenceMin?: number;
  /** Default 3.0. Linear ratio (NOT dB). */
  snrMin?: number;
}

/** Floor used to avoid divide-by-zero in linear-SNR computation. */
const SNR_EPSILON = 1e-12;

/** Reason strings exported as constants for downstream tests / consumers. */
export const REASON_PHASE_COHERENCE_LOW = 'phase_coherence_low';
export const REASON_SNR_LOW = 'snr_low';

/**
 * Argmax an envelope inside the echo search window, map to range and to a
 * range-profile bin.
 *
 * The search is restricted to samples whose round-trip range lies in
 * [minRange, maxRange]:
 *     start = ceil((tau0 + 2*minRange/c) * sampleRate)
 *     end   = floor((tau0 + 2*maxRange/c) * sampleRate)
 * This keeps the (much larger) direct-path peak near tau0 from shadowing
 * the actual echo.
 *
 * Mapping convention mirrors buildRangeProfileFromCorrelation:
 *     tau   = envelopeBin / sampleRate - tau0
 *     range = speedOfSound * tau / 2
 *     bin   = round((range - minRange) / (maxRange - minRange) * (heatBins-1)),
 *             clamped to [0, heatBins-1].
 *
 * Returns null when:
 *   - envelope is empty,
 *   - sampleRate, speedOfSound, or heatBins are non-positive / non-finite,
 *   - maxRange <= minRange,
 *   - the search window does not intersect the envelope,
 *   - the window maximum is not > 0,
 *   - or the computed range falls outside [minRange, maxRange] (rounding guard).
 */
export function findEnvelopePeak(
  args: FindEnvelopePeakArgs,
): EnvelopePeakLocation | null {
  const {
    envelope,
    tau0,
    sampleRate,
    speedOfSound,
    minRange,
    maxRange,
    heatBins,
  } = args;

  if (envelope.length === 0) return null;
  if (!(sampleRate > 0 && Number.isFinite(sampleRate))) return null;
  if (!(speedOfSound > 0 && Number.isFinite(speedOfSound))) return null;
  if (!(heatBins > 0 && Number.isFinite(heatBins))) return null;
  if (!(Number.isFinite(minRange) && Number.isFinite(maxRange))) return null;
  if (!(maxRange > minRange)) return null;
  if (!Number.isFinite(tau0)) return null;

  const windowStart = Math.max(0, Math.ceil((tau0 + (2 * minRange) / speedOfSound) * sampleRate));
  const windowEnd = Math.min(envelope.length - 1, Math.floor((tau0 + (2 * maxRange) / speedOfSound) * sampleRate));
  if (windowStart > windowEnd) return null;

  // argmax envelope within the echo window only.
  let envelopeBin = windowStart;
  let envelopeValue = envelope[windowStart];
  for (let i = windowStart + 1; i <= windowEnd; i++) {
    if (envelope[i] > envelopeValue) {
      envelopeValue = envelope[i];
      envelopeBin = i;
    }
  }
  // An all-zero window has no peak.
  if (!(envelopeValue > 0)) return null;

  const tau = envelopeBin / sampleRate - tau0;
  const range = (speedOfSound * tau) / 2;

  if (range < minRange || range > maxRange) return null;

  const span = maxRange - minRange;
  const binPos = ((range - minRange) / span) * (heatBins - 1);
  let rangeBin = Math.round(binPos);
  if (rangeBin < 0) rangeBin = 0;
  if (rangeBin > heatBins - 1) rangeBin = heatBins - 1;

  return {
    envelopeBin,
    envelopeValue,
    rangeBin,
    range,
    windowStart,
    windowEnd,
  };
}

/**
 * Estimate the noise floor of an envelope inside the echo search window,
 * excluding a guard region of `guardHalfWidth` samples on each side of the
 * peak (so the peak's main lobe does not inflate the estimate).
 *
 * Returns the median of the remaining samples; 0 when the exclusion empties
 * the window or the bounds are degenerate. Pair the result with the peak from
 * {@link findEnvelopePeak} so both sides of the SNR ratio come from the SAME
 * array at the SAME scale.
 */
export function envelopeNoiseFloorInWindow(
  envelope: Float32Array,
  windowStart: number,
  windowEnd: number,
  peakIndex: number,
  guardHalfWidth = 32,
): number {
  const start = Math.max(0, windowStart);
  const end = Math.min(envelope.length - 1, windowEnd);
  if (start > end) return 0;

  const values: number[] = [];
  for (let i = start; i <= end; i++) {
    if (Math.abs(i - peakIndex) <= guardHalfWidth) continue;
    values.push(envelope[i]);
  }
  if (values.length === 0) return 0;

  values.sort((a, b) => a - b);
  const mid = values.length >> 1;
  if ((values.length & 1) === 1) return values[mid];
  return 0.5 * (values[mid - 1] + values[mid]);
}

/**
 * Evaluate whether a detection peak passes the gate.
 *
 * pass = phaseCoherence >= coherenceMin
 *        && peakValue   >= snrMin * max(profileMedian, eps)
 *
 * Reasons array lists every failed check ('phase_coherence_low',
 * 'snr_low'); empty when pass=true.
 */
export function evaluateDetection(
  args: EvaluateDetectionArgs,
): DetectionQuality {
  const {
    phaseCoherence,
    peakValue,
    profileMedian,
    coherenceMin = 0.4,
    snrMin = 3.0,
  } = args;

  const denom = Math.max(profileMedian, SNR_EPSILON);
  const snr = peakValue / denom;

  const reasons: string[] = [];
  const coherenceOk = phaseCoherence >= coherenceMin;
  const snrOk = peakValue >= snrMin * denom;

  if (!coherenceOk) reasons.push(REASON_PHASE_COHERENCE_LOW);
  if (!snrOk) reasons.push(REASON_SNR_LOW);

  return {
    pass: coherenceOk && snrOk,
    phaseCoherence,
    snr,
    reasons,
  };
}
